import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const authHeaders = token => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });
const statusBadge = s => s === 'Active' ? 'badge badge-green' : 'badge badge-amber';

const ENGLISH_COUNTRIES = ['United States','United Kingdom','Australia','Canada','Ireland','New Zealand','Singapore','Nigeria','South Africa','Ghana','Kenya','Jamaica','Trinidad and Tobago','Barbados','Uganda','The Gambia','Papua New Guinea','Malta','Gibraltar','Puerto Rico'];

const COUNTRY_FILTERS = [
  { value: 'english', label: '🇬🇧 English-speaking countries' },
  { value: 'all',     label: '🌍 All countries' },
  { value: 'United States',  label: '🇺🇸 United States' },
  { value: 'United Kingdom', label: '🇬🇧 United Kingdom' },
  { value: 'Australia',      label: '🇦🇺 Australia' },
  { value: 'Canada',         label: '🇨🇦 Canada' },
  { value: 'Germany',        label: '🇩🇪 Germany' },
  { value: 'France',         label: '🇫🇷 France' },
  { value: 'Spain',          label: '🇪🇸 Spain' },
  { value: 'Nigeria',        label: '🇳🇬 Nigeria' },
  { value: 'South Africa',   label: '🇿🇦 South Africa' },
];

const DEFAULT_SEQUENCES = [
  {
    id: 'club', name: 'Club Onboarding Sequence', status: 'Active', enrolled: 3533, completed: 0, replies: 0,
    contactType: 'Club',
    steps: [
      { day:0,  type:'Email', subject:'Welcome to Transfer Expo', body:'Hi [First Name],\n\nYou have successfully created your Transfer Expo account. We\'re so excited to have you on board.\n\nKeep an eye out for a call from an Account Manager who will be contacting you shortly to get you started.\n\nTransfer Expo Team', cta:'', ctaUrl:'' },
      { day:1,  type:'Email', subject:'Transfer Expo is live — your club gets free access', body:'Hi [First Name],\n\nTransfer Expo is giving clubs direct access to pre-evaluated players and scouting management in one platform.\n\nYou can create your club profile, post anonymous player requests, and start discovering talent immediately.', cta:'Claim Club Profile', ctaUrl:'https://club.transferexpo.com' },
      { day:3,  type:'Email', subject:'How clubs are finding players faster', body:'Hi [First Name],\n\nClubs using structured scouting systems are reducing recruitment time significantly.\n\nTransfer Expo helps you discover pre-evaluated players, eliminating guesswork and speeding up decision-making.', cta:'See How It Works', ctaUrl:'https://transferexpo.com' },
      { day:5,  type:'Email', subject:'Your verified scout network is ready — claim it', body:'Hi [First Name],\n\nVerified scouts are already evaluating players on Transfer Expo.\n\nYour club can access this network and receive curated player recommendations immediately.', cta:'Create Club Account', ctaUrl:'https://club.transferexpo.com' },
      { day:6,  type:'Email', subject:'Clubs are already on Transfer Expo', body:'Hi [First Name],\n\nClubs across multiple regions are already using Transfer Expo to find talent.\n\nScouts on the platform are actively evaluating players for these clubs.', cta:'Get the App', ctaUrl:'https://transferexpo.com' },
      { day:7,  type:'Email', subject:'Early clubs get priority access', body:'Hi [First Name],\n\nClubs joining Transfer Expo now receive priority visibility and early access to talent pools.\n\nSecure your position before onboarding scales.', cta:'Join Now — Free', ctaUrl:'https://club.transferexpo.com' },
      { day:10, type:'Task', task:'Personal outreach via WhatsApp or LinkedIn — Hi [Name], I wanted to personally reach out about Transfer Expo. Happy to walk you through it in 15 minutes.', subject:'', body:'', cta:'', ctaUrl:'' },
      { day:14, type:'Email', subject:'Last chance for founding club access', body:'Hi [First Name],\n\nWe\'re closing early access for clubs on Transfer Expo.\n\nThis is your final opportunity to join as a founding club and access early platform benefits.', cta:'Activate Your Club Account', ctaUrl:'https://club.transferexpo.com' },
      { day:21, type:'Email', subject:'Should we pause here?', body:'Hi [First Name],\n\nJust checking in — is Transfer Expo not the right fit for your club at the moment?\n\nHappy to reconnect later if timing improves.', cta:'Reply to This Email', ctaUrl:'mailto:no-reply@transferexpo.com' },
    ]
  },
  {
    id: 'scout', name: 'Scout Activation Sequence', status: 'Active', enrolled: 1637, completed: 0, replies: 0,
    contactType: 'Scout',
    steps: [
      { day:0,  type:'Email', subject:'Welcome to Transfer Expo', body:'Hi [First Name],\n\nYou have successfully created your Transfer Expo account. We\'re so excited to have you on board.\n\nKeep an eye out for a call from an Account Manager who will be contacting you shortly to get you started.\n\nTransfer Expo Team', cta:'', ctaUrl:'' },
      { day:1,  type:'Email', subject:'A new way for scouts to earn', body:'Hi [First Name],\n\nTransfer Expo gives scouts the opportunity to evaluate players and earn while building a professional scouting profile.\n\nIf you have the ability to identify talent, there is now a platform to monetize it.', cta:'Download the App', ctaUrl:'https://transferexpo.com' },
      { day:2,  type:'Email', subject:'Here\'s exactly how much you can earn on Transfer Expo', body:'Hi [First Name],\n\nHere\'s how it works:\n• Earn per completed evaluation\n• Bonuses for early scouts\n• More activity = more opportunities\n\nNo guesswork, just a clear system.', cta:'See Earnings Breakdown', ctaUrl:'https://transferexpo.com' },
      { day:4,  type:'Email', subject:'100+ clubs need scouts — your region is uncovered', body:'Hi [First Name],\n\nWe\'re onboarding clubs globally, but many regions still lack active scouts.\n\nThis means early scouts get more visibility, more assignments, and more influence on player discovery.', cta:'Join Now — Free', ctaUrl:'https://transferexpo.com' },
      { day:6,  type:'Email', subject:'You\'re not scouting alone', body:'Hi [First Name],\n\nA growing network of clubs and scouts are already active on Transfer Expo.\n\nJoin a system built for structured scouting and real opportunities.', cta:'Get the App', ctaUrl:'https://transferexpo.com' },
      { day:9,  type:'Email', subject:'Earn a $20 bonus on your first evaluations', body:'Hi [First Name],\n\nThe first 50 scouts to join and complete evaluations will receive a bonus.\n\nThis is a limited opportunity to start earning immediately.', cta:'Claim Your Bonus', ctaUrl:'https://transferexpo.com' },
      { day:14, type:'Email', subject:'Should we close your file?', body:'Hi [First Name],\n\nWe haven\'t heard back — is scouting still something you want to pursue?\n\nIf yes, we can help you get started.', cta:'Reply to This Email', ctaUrl:'mailto:no-reply@transferexpo.com' },
    ]
  },
];

const BLANK_STEP = { day:0, type:'Email', subject:'', body:'', cta:'', ctaUrl:'', task:'' };

export default function Sequences({ toast }) {
  const { token } = useApp();
  const [sequences, setSequences]         = useState(DEFAULT_SEQUENCES);
  const [view, setView]                   = useState('list'); // list | detail | create | editStep
  const [activeSeq, setActiveSeq]         = useState(null);
  const [editingStep, setEditingStep]     = useState(null); // index or null
  const [stepDraft, setStepDraft]         = useState(BLANK_STEP);
  const [enrolling, setEnrolling]         = useState(null);
  const [sending, setSending]             = useState(null);
  const [serverIds, setServerIds]         = useState({});
  const [countryFilter, setCountryFilter] = useState('english');
  const [newSeq, setNewSeq]               = useState({ name:'', contactType:'Club', status:'Active', steps:[] });

  // Enrollment tracker state
  const [enrollments, setEnrollments]     = useState([]);
  const [enrollSummary, setEnrollSummary] = useState({});
  const [enrollPage, setEnrollPage]       = useState(1);
  const [enrollTotal, setEnrollTotal]     = useState(0);
  const [enrollFilter, setEnrollFilter]   = useState('');
  const [loadingEnroll, setLoadingEnroll] = useState(false);
  const [detailTab, setDetailTab]         = useState('steps');

  // Load live sequences from API
  const loadSequences = useCallback(async () => {
    try {
      const res = await fetch('/api/sequences', { headers: authHeaders(token) });
      const data = await res.json();
      if (data.data && data.data.length > 0) {
        setSequences(prev => {
          // Merge live stats into existing sequences
          return data.data.map(live => {
            const existing = prev.find(p => p.id === live.id);
            return existing ? { ...existing, enrolled: live.enrolled, completed: live.completed, replies: live.replies, status: live.status } : live;
          });
        });
      }
    } catch(e) {}
  }, [token]);

  // Load on mount and refresh every 30 seconds
  useEffect(() => {
    loadSequences();
    const interval = setInterval(loadSequences, 30000);
    return () => clearInterval(interval);
  }, [loadSequences]);


  const loadEnrollments = useCallback(async (seqId, page=1, status='') => {
    setLoadingEnroll(true);
    try {
      const res = await fetch(`/api/sequences/${seqId}/enrollments?page=${page}&limit=20${status ? '&status='+status : ''}`,
        { headers: authHeaders(token) });
      const data = await res.json();
      setEnrollments(data.data || []);
      setEnrollTotal(data.total || 0);
      setEnrollSummary(data.summary || {});
    } catch(e) {} finally { setLoadingEnroll(false); }
  }, [token]);

  useEffect(() => {
    if (view === 'detail' && activeSeq && detailTab === 'contacts') {
      loadEnrollments(activeSeq, enrollPage, enrollFilter);
    }
  }, [view, activeSeq, detailTab, enrollPage, enrollFilter]);

  const seq = sequences.find(s => s.id === activeSeq);

  // ── Enrol ──────────────────────────────────────────────────────────────────
  const handleEnroll = async (s) => {
    setEnrolling(s.id);
    try {
      const res = await fetch('/api/sequences/enroll', {
        method:'POST', headers: authHeaders(token),
        body: JSON.stringify({ name: s.name, contactType: s.contactType, steps: s.steps, countryFilter }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Enrol failed');
      setServerIds(p => ({ ...p, [s.id]: data.data?.sequenceId }));
      toast?.(`✅ ${data.data?.message}`);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setEnrolling(null); }
  };

  // ── Send ───────────────────────────────────────────────────────────────────
  const handleSend = async (s) => {
    const sequenceId = serverIds[s.id];
    if (!sequenceId) { toast?.('Enrol contacts first', 'error'); return; }
    setSending(s.id);
    try {
      const res = await fetch('/api/sequences/send', {
        method:'POST', headers: authHeaders(token),
        body: JSON.stringify({ sequenceId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Send failed');
      toast?.(`📧 ${data.data?.message}`);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setSending(null); }
  };

  // ── Toggle status ──────────────────────────────────────────────────────────
  const toggleStatus = id =>
    setSequences(p => p.map(s => s.id === id ? { ...s, status: s.status==='Active'?'Paused':'Active' } : s));

  // ── Step editor ────────────────────────────────────────────────────────────
  const openNewStep = () => { setStepDraft({ ...BLANK_STEP }); setEditingStep('new'); setView('editStep'); };
  const openEditStep = (i) => { setStepDraft({ ...seq.steps[i] }); setEditingStep(i); setView('editStep'); };
  const deleteStep = (i) => {
    setSequences(p => p.map(s => s.id === activeSeq ? { ...s, steps: s.steps.filter((_,idx)=>idx!==i) } : s));
    toast?.('Step deleted');
  };
  const saveStep = () => {
    setSequences(p => p.map(s => {
      if (s.id !== activeSeq) return s;
      const steps = [...s.steps];
      if (editingStep === 'new') steps.push(stepDraft);
      else steps[editingStep] = stepDraft;
      steps.sort((a,b) => a.day - b.day);
      return { ...s, steps };
    }));
    toast?.('Step saved');
    setView('detail');
  };

  // ── Create sequence ────────────────────────────────────────────────────────
  const createSequence = () => {
    if (!newSeq.name.trim()) { toast?.('Enter a sequence name', 'error'); return; }
    const s = { ...newSeq, id: Date.now().toString(), enrolled:0, completed:0, replies:0 };
    setSequences(p => [...p, s]);
    setActiveSeq(s.id);
    setNewSeq({ name:'', contactType:'Club', status:'Active', steps:[] });
    setView('detail');
    toast?.('Sequence created');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // VIEWS
  // ─────────────────────────────────────────────────────────────────────────

  // ── Edit step view ─────────────────────────────────────────────────────────
  if (view === 'editStep') return (
    <div>
      <button className="btn btn-ghost" style={{ marginBottom:16, fontSize:13 }} onClick={() => setView('detail')}>← Back to sequence</button>
      <div className="card" style={{ maxWidth:680 }}>
        <div className="card-header" style={{ marginBottom:20 }}>
          <span className="card-title">{editingStep === 'new' ? 'Add step' : 'Edit step'}</span>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
          <div>
            <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>Step type</label>
            <select value={stepDraft.type} onChange={e => setStepDraft(p=>({...p, type:e.target.value}))}
              style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }}>
              <option value="Email">Email</option>
              <option value="Task">Task (manual)</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>Send on day</label>
            <input type="number" min="0" value={stepDraft.day} onChange={e => setStepDraft(p=>({...p, day:parseInt(e.target.value)||0}))}
              style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }} />
          </div>
        </div>

        {stepDraft.type === 'Email' ? (
          <>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>Subject line</label>
              <input value={stepDraft.subject} onChange={e => setStepDraft(p=>({...p, subject:e.target.value}))}
                placeholder="e.g. Welcome to Transfer Expo"
                style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>Email body <span style={{color:'var(--accent)'}}>— use [First Name] for personalisation</span></label>
              <textarea value={stepDraft.body} onChange={e => setStepDraft(p=>({...p, body:e.target.value}))}
                rows={8} placeholder="Hi [First Name],&#10;&#10;Your message here..."
                style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'10px', resize:'vertical', lineHeight:1.7 }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <div>
                <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>CTA button text (optional)</label>
                <input value={stepDraft.cta} onChange={e => setStepDraft(p=>({...p, cta:e.target.value}))}
                  placeholder="e.g. Claim Club Profile"
                  style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>CTA URL</label>
                <input value={stepDraft.ctaUrl} onChange={e => setStepDraft(p=>({...p, ctaUrl:e.target.value}))}
                  placeholder="https://club.transferexpo.com"
                  style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }} />
              </div>
            </div>
          </>
        ) : (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>Task description</label>
            <textarea value={stepDraft.task} onChange={e => setStepDraft(p=>({...p, task:e.target.value}))}
              rows={4} placeholder="e.g. Call the club rep to confirm onboarding..."
              style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'10px', resize:'vertical' }} />
          </div>
        )}

        {/* Preview */}
        {stepDraft.type === 'Email' && stepDraft.subject && (
          <div style={{ marginBottom:16, padding:16, background:'var(--bg-input)', borderRadius:8, border:'1px solid var(--border)' }}>
            <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:8 }}>📧 Preview</div>
            <div style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)', marginBottom:8 }}>{stepDraft.subject}</div>
            <div style={{ fontSize:13, color:'var(--text-muted)', whiteSpace:'pre-line', lineHeight:1.7 }}>{stepDraft.body.replace('[First Name]','Hussein')}</div>
            {stepDraft.cta && <div style={{ marginTop:12, display:'inline-block', background:'#A7F432', color:'#0a0a0a', padding:'8px 16px', borderRadius:6, fontSize:12, fontWeight:600 }}>{stepDraft.cta}</div>}
          </div>
        )}

        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-primary" onClick={saveStep}>Save step</button>
          <button className="btn btn-ghost" onClick={() => setView('detail')}>Cancel</button>
        </div>
      </div>
    </div>
  );

  // ── Create sequence view ───────────────────────────────────────────────────
  if (view === 'create') return (
    <div>
      <button className="btn btn-ghost" style={{ marginBottom:16, fontSize:13 }} onClick={() => setView('list')}>← Back to sequences</button>
      <div className="card" style={{ maxWidth:560 }}>
        <div className="card-header" style={{ marginBottom:20 }}>
          <span className="card-title">Create new sequence</span>
        </div>
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>Sequence name</label>
          <input value={newSeq.name} onChange={e => setNewSeq(p=>({...p, name:e.target.value}))}
            placeholder="e.g. Re-engagement Sequence"
            style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }} />
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
          <div>
            <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>Contact type</label>
            <select value={newSeq.contactType} onChange={e => setNewSeq(p=>({...p, contactType:e.target.value}))}
              style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }}>
              <option>Club</option>
              <option>Scout</option>
              <option>Player</option>
              <option>Agent</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>Status</label>
            <select value={newSeq.status} onChange={e => setNewSeq(p=>({...p, status:e.target.value}))}
              style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }}>
              <option>Active</option>
              <option>Paused</option>
            </select>
          </div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-primary" onClick={createSequence}>Create sequence</button>
          <button className="btn btn-ghost" onClick={() => setView('list')}>Cancel</button>
        </div>
      </div>
    </div>
  );

  // ── Detail view ────────────────────────────────────────────────────────────
  if (view === 'detail' && seq) return (
    <div>
      <button className="btn btn-ghost" style={{ marginBottom:16, fontSize:13 }} onClick={() => setView('list')}>← Back to sequences</button>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:500, color:'var(--text-primary)', marginBottom:4 }}>{seq.name}</h2>
          <div style={{ display:'flex', gap:12, fontSize:12, color:'var(--text-muted)' }}>
            <span><strong style={{color:'var(--text-primary)'}}>{seq.contactType}</strong> contacts</span>
            <span><strong style={{color:'var(--text-primary)'}}>{seq.steps.length}</strong> steps</span>
            <span><strong style={{color:'var(--text-primary)'}}>{(seq.enrolled||0).toLocaleString()}</strong> enrolled</span>
            <span className={statusBadge(seq.status)}>{seq.status}</span>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-ghost" style={{ fontSize:12 }} onClick={() => toggleStatus(seq.id)}>
            {seq.status === 'Active' ? 'Pause' : 'Resume'}
          </button>
        </div>
      </div>

      {/* Country filter + launch */}
      <div className="card" style={{ marginBottom:20, padding:'16px 20px' }}>
        <div style={{ fontSize:13, fontWeight:500, color:'var(--text-primary)', marginBottom:12 }}>🚀 Launch settings</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr auto auto', gap:10, alignItems:'end' }}>
          <div>
            <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6 }}>Target region</label>
            <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
              style={{ width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }}>
              {COUNTRY_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" disabled={enrolling===seq.id} onClick={() => handleEnroll(seq)} style={{ whiteSpace:'nowrap' }}>
            {enrolling===seq.id ? 'Enrolling…' : '+ Enrol contacts'}
          </button>
          <button className="btn btn-ghost" disabled={sending===seq.id} onClick={() => handleSend(seq)}
            style={{ whiteSpace:'nowrap', borderColor:'var(--accent)', color:'var(--accent)' }}>
            {sending===seq.id ? 'Sending…' : '📧 Send due emails'}
          </button>
        </div>
      </div>

      {/* Steps timeline */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <span style={{ fontSize:14, fontWeight:500, color:'var(--text-primary)' }}>Sequence steps</span>
        <button className="btn btn-primary" style={{ fontSize:12 }} onClick={openNewStep}>+ Add step</button>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:20, borderBottom:'1px solid var(--border)' }}>
        {[['steps','📋 Steps'],['contacts','👥 Contacts']].map(([t,l]) => (
          <button key={t} onClick={() => setDetailTab(t)}
            style={{ padding:'8px 20px', fontSize:13, border:'none', cursor:'pointer', background:'none',
              color: detailTab===t ? 'var(--accent)' : 'var(--text-dim)',
              borderBottom: detailTab===t ? '2px solid var(--accent)' : '2px solid transparent',
              fontWeight: detailTab===t ? 600 : 400 }}>
            {l}
          </button>
        ))}
      </div>

      {detailTab === 'contacts' && (
        <div>
          {/* Summary */}
          <div className="stats-grid" style={{ marginBottom:16 }}>
            <div className="stat-card"><div className="stat-label">Total enrolled</div><div className="stat-value">{enrollTotal}</div></div>
            <div className="stat-card"><div className="stat-label">Active</div><div className="stat-value" style={{color:'var(--accent)'}}>{enrollSummary.active||0}</div><div className="stat-sub stat-up">In sequence</div></div>
            <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value" style={{color:'#4fc3f7'}}>{enrollSummary.completed||0}</div><div className="stat-sub">Finished</div></div>
            <div className="stat-card"><div className="stat-label">Unsubscribed</div><div className="stat-value" style={{color:'var(--danger)'}}>{enrollSummary.unsubscribed||0}</div></div>
          </div>

          {/* Filter */}
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            {['','active','completed','unsubscribed'].map(s => (
              <button key={s} onClick={() => { setEnrollFilter(s); setEnrollPage(1); }}
                style={{ padding:'4px 12px', borderRadius:20, fontSize:12, border:'1px solid var(--border)', cursor:'pointer',
                  background: enrollFilter===s ? 'var(--accent)' : 'var(--bg-input)',
                  color: enrollFilter===s ? '#0a0a0a' : 'var(--text-dim)' }}>
                {s || 'All'}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="card" style={{ padding:0 }}>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Contact</th><th>Company</th><th>Step</th><th>Status</th><th>Next send</th></tr></thead>
                <tbody>
                  {loadingEnroll ? (
                    <tr><td colSpan={5} style={{ textAlign:'center', padding:32, color:'var(--text-dim)' }}>Loading…</td></tr>
                  ) : enrollments.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign:'center', padding:32, color:'var(--text-dim)' }}>No contacts found</td></tr>
                  ) : enrollments.map(e => (
                    <tr key={e.id}>
                      <td>
                        <div style={{ fontWeight:500, fontSize:13 }}>{e.contact?.firstName} {e.contact?.lastName}</div>
                        <div style={{ fontSize:11, color:'var(--text-dim)' }}>{e.contact?.email}</div>
                      </td>
                      <td style={{ fontSize:12, color:'var(--text-muted)' }}>{e.contact?.company||'—'}</td>
                      <td style={{ fontSize:12 }}>Step {e.currentStep + 1} of {(seq.steps||[]).length}</td>
                      <td><span className={({'active':'badge badge-green','completed':'badge badge-blue','unsubscribed':'badge badge-red'})[e.status]||'badge badge-gray'}>{e.status}</span></td>
                      <td style={{ fontSize:11, color:'var(--text-dim)' }}>{e.status==='active' ? new Date(e.nextSendAt).toLocaleDateString([], {month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {Math.ceil(enrollTotal/20) > 1 && (
            <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:12 }}>
              <button className="btn btn-ghost" disabled={enrollPage<=1} onClick={() => setEnrollPage(p=>p-1)}>← Prev</button>
              <span style={{ padding:'8px 14px', fontSize:13, color:'var(--text-muted)' }}>Page {enrollPage} / {Math.ceil(enrollTotal/20)}</span>
              <button className="btn btn-ghost" disabled={enrollPage>=Math.ceil(enrollTotal/20)} onClick={() => setEnrollPage(p=>p+1)}>Next →</button>
            </div>
          )}
        </div>
      )}

      {detailTab === 'steps' && <div style={{ position:'relative', paddingLeft:28 }}>
        <div style={{ position:'absolute', left:9, top:0, bottom:0, width:2, background:'var(--border)' }} />
        {(seq.steps||[]).length === 0 && (
          <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text-dim)', fontSize:13 }}>
            No steps yet — click "+ Add step" to build your sequence
          </div>
        )}
        {(seq.steps||[]).map((step, i) => (
          <div key={i} style={{ position:'relative', marginBottom:14 }}>
            <div style={{ position:'absolute', left:-28, top:12, width:16, height:16, borderRadius:'50%', background: step.type==='Email' ? 'var(--accent)' : '#4fc3f7', border:'2px solid var(--bg-page)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:8 }}>
              {step.type==='Email' ? '✉' : '✓'}
            </div>
            <div className="card" style={{ padding:'14px 16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                    <span style={{ fontSize:11, color:'var(--text-dim)', background:'var(--bg-input)', padding:'2px 8px', borderRadius:20 }}>Day {step.day}</span>
                    <span className={step.type==='Email' ? 'badge badge-green' : 'badge badge-blue'} style={{ fontSize:10 }}>{step.type}</span>
                  </div>
                  <div style={{ fontSize:13, fontWeight:500, color:'var(--text-primary)', marginBottom: step.cta ? 4 : 0 }}>
                    {step.type==='Email' ? step.subject : step.task}
                  </div>
                  {step.body && <div style={{ fontSize:12, color:'var(--text-dim)', marginTop:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:480 }}>{step.body.split('\n')[2] || step.body.split('\n')[0]}</div>}
                  {step.cta && <div style={{ fontSize:11, color:'var(--accent)', marginTop:4 }}>→ {step.cta}</div>}
                </div>
                <div style={{ display:'flex', gap:6, marginLeft:12 }}>
                  <button className="btn btn-ghost" style={{ fontSize:11, padding:'3px 10px' }} onClick={() => openEditStep(i)}>Edit</button>
                  <button className="btn btn-ghost" style={{ fontSize:11, padding:'3px 10px', color:'var(--danger)' }} onClick={() => deleteStep(i)}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>}
    </div>
  );

  // ── List view (default) ────────────────────────────────────────────────────
  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Active sequences</div><div className="stat-value">{sequences.filter(s=>s.status==='Active').length}</div><div className="stat-sub stat-up">Running</div></div>
        <div className="stat-card"><div className="stat-label">Total enrolled</div><div className="stat-value">{sequences.reduce((a,s)=>a+s.enrolled,0).toLocaleString()}</div><div className="stat-sub">All sequences</div></div>
        <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value">{sequences.reduce((a,s)=>a+s.completed,0).toLocaleString()}</div><div className="stat-sub stat-up">Finished</div></div>
        <div className="stat-card"><div className="stat-label">Replies</div><div className="stat-value">{sequences.reduce((a,s)=>a+s.replies,0)}</div><div className="stat-sub">Total</div></div>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:500, color:'var(--text-primary)' }}>Email sequences</h3>
        <button className="btn btn-primary" onClick={() => setView('create')}>+ Create sequence</button>
      </div>

      {sequences.map(s => (
        <div key={s.id} className="card" style={{ marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
            <div>
              <div style={{ fontWeight:500, fontSize:14, color:'var(--text-primary)', marginBottom:4, cursor:'pointer' }}
                onClick={() => { setActiveSeq(s.id); setView('detail'); }}>
                {s.name}
              </div>
              <div style={{ fontSize:12, color:'var(--text-dim)' }}>{s.contactType} · {s.steps.length} steps</div>
            </div>
            <span className={statusBadge(s.status)}>{s.status}</span>
          </div>
          <div style={{ display:'flex', gap:20, fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>
            <span><strong style={{color:'var(--text-primary)'}}>{s.enrolled.toLocaleString()}</strong> enrolled</span>
            <span><strong style={{color:'var(--accent)'}}>{s.completed.toLocaleString()}</strong> completed</span>
            <span><strong style={{color:'#4fc3f7'}}>{s.replies}</strong> replies</span>
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            <button className="btn btn-ghost" style={{ fontSize:11, padding:'4px 10px' }}
              onClick={() => { setActiveSeq(s.id); setView('detail'); }}>
              Open →
            </button>
            <button className="btn btn-ghost" style={{ fontSize:11, padding:'4px 10px' }} onClick={() => toggleStatus(s.id)}>
              {s.status==='Active' ? 'Pause' : 'Resume'}
            </button>
            <button className="btn btn-primary" style={{ fontSize:11, padding:'4px 12px' }}
              disabled={enrolling===s.id} onClick={() => handleEnroll(s)}>
              {enrolling===s.id ? 'Enrolling…' : '+ Enrol contacts'}
            </button>
            <button className="btn btn-ghost" style={{ fontSize:11, padding:'4px 12px', borderColor:'var(--accent)', color:'var(--accent)' }}
              disabled={sending===s.id} onClick={() => handleSend(s)}>
              {sending===s.id ? 'Sending…' : '📧 Send due emails'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
